import React from 'react';
import { useDispatch } from 'react-redux';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';

import { useTranslation } from 'src/i18n';
import { Button } from 'src/component/ds/ui/button';
import { cn } from 'src/component/ds/utils';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from 'src/component/ds/ui/empty';
import { clearSelectedFilters } from 'src/module/filter/action';
import type { DeviceListScope } from 'src/service/api/deviceList';

function scopeKind (scope?: DeviceListScope) {
  if (scope?.controllerId) return 'controller';
  if (scope?.panelId === 'other') return 'unassigned';
  if (scope?.panelId) return 'panel';
  if (scope?.commLoopId === 'other') return 'unassignedConnection';
  if (scope?.commLoopId) return 'connection';
  if (scope?.groupId) return 'group';
  return 'facility';
}

interface EmptyLayoutProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: { label: string; onClick: () => void };
  className?: string;
}

function EmptyLayout ({ icon, title, description, action, className }: EmptyLayoutProps) {
  return (
    <Empty className={cn('ds-text-wrap', className)}>
      <EmptyMedia variant='icon'>{icon}</EmptyMedia>
      <EmptyHeader className='ds-max-w-[356px]'>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{description}</EmptyDescription>
      </EmptyHeader>
      {action && (
        <EmptyContent>
          <Button className='ds-px-4' onClick={action.onClick}>{action.label}</Button>
        </EmptyContent>
      )}
    </Empty>
  );
}

interface NoDevicesYetProps {
  /** Unset for the whole facility. */
  scope?: DeviceListScope;
  onViewAllDevices?: () => void;
  className?: string;
}

/* No filters are applied and the scope itself is empty. */
export function NoDevicesYet ({ scope, onViewAllDevices, className }: NoDevicesYetProps) {
  const { t } = useTranslation();

  return (
    <EmptyLayout
      icon={<WidgetsOutlinedIcon className='ds-size-6' aria-hidden='true' />}
      title={t('deviceList.emptyScope.title')}
      description={t(`deviceList.emptyScope.description.${scopeKind(scope)}`)}
      action={onViewAllDevices && { label: t('deviceList.emptyScope.viewDevices'), onClick: onViewAllDevices }}
      className={className}
    />
  );
}

/* Filters are applied and nothing matches: the scope may well have devices. */
export function NoMatchingDevices ({ className }: { className?: string }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  return (
    <EmptyLayout
      icon={<FilterAltOffOutlinedIcon className='ds-size-6' aria-hidden='true' />}
      title={t('deviceList.noMatches.title')}
      description={t('deviceList.noMatches.description')}
      action={{ label: t('deviceList.clearAll'), onClick: () => dispatch(clearSelectedFilters()) }}
      className={className}
    />
  );
}
